import { MultiSelect } from "primereact/multiselect";
import * as client from "../../../client";
import { Button } from "primereact/button";
import { useState } from "react";
import { SovHelperModal } from "./SovHelperModal";
import styles from "./EditSheets.module.css";
import { InUseDecoration } from "../../../components/in-use-decoration/InUseDecoration";
import { Tooltip } from "primereact/tooltip";
import { v4 as uuid } from "uuid";
import { useThisReportIsUpdating, useUpdateReport } from "../data";
import { useRerunningMessage } from "../RerunUI";
import { Checkbox } from "primereact/checkbox";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";

export const EditSheets: React.FC<{
  reportId: string;
  sovs: client.Sov[];
}> = ({ reportId, sovs }) => {
  const updateReport = useUpdateReport(reportId);
  const isRerunningRemotely = useThisReportIsUpdating(reportId);
  const rerunningMessage = useRerunningMessage({
    update: isRerunningRemotely?.updating,
  });
  const filenames = sovs
    .filter((sov) => !sov.is_manual)
    .map((sov) => {
      return {
        name: sov.filename,
        tivMatch: sov.tiv_totals_match_on_ingest,
        tivSource: sov.tiv_target_source,
        tivExplanation: sov.tiv_reconciliation_explanation,
      };
    });

  const selectedFilenames = sovs
    .filter((sov) => sov.is_enabled && !sov.is_manual)
    .map((sov) => {
      return {
        name: sov.filename,
        tivMatch: sov.tiv_totals_match_on_ingest,
        tivSource: sov.tiv_target_source,
        tivExplanation: sov.tiv_reconciliation_explanation,
      };
    });
  // Default to multi-select mode if multiple are already selected
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(
    selectedFilenames.length > 1
  );
  const [helperModalVisible, setHelperModalVisible] = useState(false);

  return (
    <div className={styles.container}>
      <SovHelperModal
        reportId={reportId}
        visible={helperModalVisible}
        setVisible={setHelperModalVisible}
      />
      <MultiSelect
        panelHeaderTemplate={
          <div
            style={{
              padding: "16px 20px",
              borderBottom: "1px solid var(--surface-border)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Checkbox
                disabled={
                  updateReport.isPending || selectedFilenames.length > 1
                }
                inputId="multi-select-toggle"
                checked={isMultiSelectMode}
                onChange={(e) => setIsMultiSelectMode(e.checked ?? false)}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
              <label
                htmlFor="multi-select-toggle"
                style={{ cursor: "pointer" }}
              >
                Allow multiple SOV sources to be selected
              </label>
            </div>
          </div>
        }
        panelFooterTemplate={
          <Button
            label="Don't see your file?"
            text
            style={{ marginLeft: "12px", marginBottom: "12px" }}
            onClick={() => setHelperModalVisible(true)}
          />
        }
        disabled={updateReport.isPending || !!rerunningMessage}
        tooltip={rerunningMessage}
        tooltipOptions={{ showDelay: 500, showOnDisabled: true }}
        value={selectedFilenames}
        onChange={(e) => {
          const oldFilenames = selectedFilenames.map(
            (filename) => filename.name
          );
          const enabledFilenames = (e.value as { name: string }[]).map(
            (filename) => filename.name
          );
          const newlyEnabledNames = enabledFilenames.filter(
            (name) => !oldFilenames.includes(name)
          );
          const newlyEnabledName =
            newlyEnabledNames.length === 1 ? newlyEnabledNames[0] : undefined;
          const sovsUpdate = sovs.map((sov) => ({
            ...sov,
            is_enabled:
              !isMultiSelectMode && newlyEnabledName
                ? newlyEnabledName === sov.filename
                : enabledFilenames.some((name) => name === sov.filename),
          }));
          updateReport.mutate({ sovs: sovsUpdate });
        }}
        scrollHeight={"500px"}
        options={filenames}
        optionLabel="name"
        placeholder="Select Files/Sheets"
        style={{ width: "260px" }}
        itemTemplate={(option) => {
          const tooltipId = `tooltip-${uuid()}`;
          return (
            <div className={styles.horizontalContainer}>
              {option.tivMatch === true && (
                <TooltipWrapper
                  tooltipText={option.tivExplanation ?? "TIV totals match"}
                  maxWidth={600}
                >
                  <i
                    className="pi pi-check-circle"
                    style={{
                      // An email-sourced TIV target is more trustworthy than
                      // one derived from an SOV spreadsheet, so only the former
                      // gets the reassuring green. SOV-sourced matches use the
                      // default text color.
                      color:
                        option.tivSource === "email"
                          ? "var(--green-500)"
                          : undefined,
                      fontSize: "0.85rem",
                    }}
                  />
                </TooltipWrapper>
              )}
              {option.tivMatch === false && (
                <TooltipWrapper
                  tooltipText={
                    option.tivExplanation ?? "TIV totals do not match"
                  }
                  maxWidth={600}
                >
                  <i
                    className="pi pi-times-circle"
                    style={{ color: "var(--red-500)", fontSize: "0.85rem" }}
                  />
                </TooltipWrapper>
              )}
              {option.name}
              {option.name === "Smart Parse" && (
                <>
                  <Tooltip
                    target={`#${tooltipId}`}
                    showDelay={500}
                    style={{ maxWidth: "500px" }}
                  />
                  <i
                    className="pi pi-info-circle"
                    data-pr-tooltip={
                      "Smart Parse looks at all files simultaneously when extracting information. This generally works better for small files with data spread across multiple sources."
                    }
                    id={tooltipId}
                  />
                </>
              )}
            </div>
          );
        }}
        showSelectAll={false}
      />
      <div className={styles.decorationWrapper}>
        {sovs.length > 1 ? <InUseDecoration count={sovs.length} /> : null}
      </div>
    </div>
  );
};
